/**
 * AppShell (TMMAPP-0004, design §14.3; the v2 shell, TMMAPP-0150 U2) — the layout route under
 * `MeGate`: the sidebar (permanent at `lg` and up, a temporary drawer below), then the column of the
 * v2 top bar (`TopBar`: the date, the people and the account menu) over the
 * content outlet. It mounts the shell-wide providers: the header content pages publish with
 * `useHeader()`, the shared virtual-accounts-list fetch the Virtual accounts page reads through
 * `useVirtualAccountsList()`, the one unsaved-changes blocker the editors register with
 * (`UnsavedGuardProvider`, TMMAPP-0073), and the team's announcements (TMMAPP-0142 U2). On first
 * load it drops Viewing's leftover `tmm_viewing` key (TMMAPP-0159 §A 1).
 */
import { useEffect, useState } from 'react';
import { Alert, Box, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { VirtualAccountsListProvider } from '@/features/virtualAccounts/VirtualAccountsListProvider';
import { AnnouncementsProvider } from '@/features/announcements/AnnouncementsProvider';
import { AiContributionNotice } from '@/features/assistant/AiContributionNotice';
import { GuestTour } from '@/features/onboarding/GuestTour';
import { SubscribeOfferProvider } from '@/features/subscribe/SubscribeOfferProvider';
import { NamingArrivalPrompt } from '@/features/payees/NamingArrivalPrompt';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { isLockedExemptRoute, LockedScreen } from '@/session/LockedScreen';
import { useMe } from '@/session/meHooks';
import { UnsavedGuardProvider } from '@/shared/UnsavedGuardProvider';
import { AccountMenu } from './AccountMenu';
import { HeaderContextProvider } from './HeaderContextProvider';
import { Sidebar } from './Sidebar';
import { FULL_HEIGHT_SX } from './barHeight';
import { MAIN_ID, SkipLink } from './SkipLink';
import { TopBar } from './TopBar';
import { forgetLeftoverViewing } from './leftoverViewing';

function ShellLayout() {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  // Visual contract v2 §15: as drawn at `lg` and up; the sidebar is a drawer at `md` and below.
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const frozenWhen =
    me.frozenAt !== null
      ? (() => {
          try {
            return formatInstant(me.frozenAt, locale, zone);
          } catch {
            return me.frozenAt;
          }
        })()
      : '—';
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const closeNav = () => setNavOpen(false);

  return (
    <Box sx={{ display: 'flex', ...FULL_HEIGHT_SX }}>
      <SkipLink />
      <Sidebar me={me} wide={wide} open={navOpen} onClose={closeNav} onNavigate={closeNav} />
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          me={me}
          wide={wide}
          navOpen={navOpen}
          onOpenNav={() => setNavOpen(true)}
          menuOpen={menuAnchor !== null}
          onOpenMenu={setMenuAnchor}
        />
        <AccountMenu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} />
        <Box
          component="main"
          id={MAIN_ID}
          tabIndex={-1}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            // The column fills what the top bar leaves, so a full-height page (the assistant) fills
            // it with flex, never a `100dvh - bar` sum (TMMAPP-0154 §A 4).
            display: 'flex',
            flexDirection: 'column',
            '&:focus': { outline: 'none' },
          }}
        >
          {/* Visual contract v2 §1 / §15: the page padding is 32 (16 at `xs`), the sections in a
              column at gap 24 — also inside a page whose root box holds its header. Settings caps
              its own width. */}
          <Box
            data-main-column=""
            sx={{
              flexGrow: 1,
              p: { xs: 2, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              minWidth: 0,
              '& > div:has(> [data-page-header])': {
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              },
            }}
          >
            {me.frozen && (
              <Alert severity="warning">{t('shell.frozen', { when: frozenWhen })}</Alert>
            )}
            <AiContributionNotice />
            {me.locked && !isLockedExemptRoute(location.pathname) ? <LockedScreen /> : <Outlet />}
          </Box>
          <GuestTour />
        </Box>
      </Box>
      <NamingArrivalPrompt />
    </Box>
  );
}

export function AppShell() {
  useEffect(forgetLeftoverViewing, []);
  return (
    <UnsavedGuardProvider>
      <HeaderContextProvider>
        <SubscribeOfferProvider>
          <VirtualAccountsListProvider>
            <AnnouncementsProvider>
              <ShellLayout />
            </AnnouncementsProvider>
          </VirtualAccountsListProvider>
        </SubscribeOfferProvider>
      </HeaderContextProvider>
    </UnsavedGuardProvider>
  );
}
